import {
  Checkbox,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanelList,
  Text,
  TextLink,
  Tooltip,
  UnderlineTabs,
} from '@modusign/design-system';
import {
  getListGroupsQueryKey,
  getListMembersQueryKey,
  listGroups,
  listMembers,
} from '@modusign/openapi-client-user';
import { ReactNode, useMemo } from 'react';
import { useQuery } from 'react-query';

import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

import { ResourceRoleSubject } from '../../types';
import Badge from '../Badge';
import ResourceRoleSubjectLabel from '../ResourceRoleSubjectLabel';

interface SearchedMemberGroupTabProps {
  disabled?: boolean;
  keyword: string;
  hiddenSubjects?: ResourceRoleSubject[];
  selectedSubjects: ResourceRoleSubject[];
  shouldHideAllMembers?: boolean;
  tooltipContent?: ReactNode;
  unavailableSubjects?: ResourceRoleSubject[];
  onToggle: (value: ResourceRoleSubject) => void;
}

export default function SearchedMemberGroupTab({
  disabled,
  hiddenSubjects,
  keyword,
  selectedSubjects,
  shouldHideAllMembers,
  tooltipContent,
  unavailableSubjects,
  onToggle,
}: SearchedMemberGroupTabProps) {
  const workspaceId = useWorkspaceId();

  const selectedSubjectIds = useMemo(
    () => new Set(selectedSubjects.map(({ subjectId }) => subjectId)),
    [selectedSubjects]
  );
  const unavailableSubjectIds = useMemo(
    () => new Set(unavailableSubjects?.map(({ subjectId }) => subjectId)),
    [unavailableSubjects]
  );

  const {
    data: groupSubjects,
    isError: isGroupQueryError,
    refetch: groupRefetch,
  } = useQuery({
    queryKey: getListGroupsQueryKey(workspaceId, { name: keyword }),
    cacheTime: 30,
    queryFn: () => listGroups(workspaceId, { name: keyword }),
    keepPreviousData: true,
  });

  const groupSubjectsModel = groupSubjects?.groups
    .filter((g) => !hiddenSubjects?.some(({ subjectId }) => subjectId === g.id))
    .filter((g) => (shouldHideAllMembers ? g.type !== 'ALL_MEMBERS' : true))
    .map((group) => ({
      role: {
        subjectType: 'GROUP' as const,
        subjectId: group.id,
        groupName: group.name,
        groupType: group.type,
      },
      disabled:
        (disabled && !selectedSubjectIds.has(group.id)) || unavailableSubjectIds.has(group.id),
      isSelected: selectedSubjectIds.has(group.id) || unavailableSubjectIds.has(group.id),
      tooltipContent: unavailableSubjectIds.has(group.id) ? '이미 추가됨' : tooltipContent,
    }));

  const {
    data: memberSubjects,
    isError: isMemberQueryError,
    refetch: memberRefetch,
  } = useQuery({
    queryKey: getListMembersQueryKey(workspaceId, { name: keyword }),
    cacheTime: 30,
    queryFn: () => listMembers(workspaceId, { name: keyword }),
    keepPreviousData: true,
  });

  const memberSubjectsModel = memberSubjects?.members
    .filter((m) => !hiddenSubjects?.some(({ subjectId }) => subjectId === m.id))
    .map((member) => ({
      role: {
        subjectType: 'MEMBER' as const,
        subjectId: member.id,
        memberName: member.name,
        memberEmail: member.userEmail,
      },
      disabled:
        (disabled && !selectedSubjectIds.has(member.id)) || unavailableSubjectIds.has(member.id),
      isSelected: selectedSubjectIds.has(member.id) || unavailableSubjectIds.has(member.id),
      tooltipContent: unavailableSubjectIds.has(member.id) ? '이미 추가됨' : tooltipContent,
    }));

  return (
    <UnderlineTabs flexDirection="column">
      <TabList>
        <Tab>
          그룹 <Badge size="small">{groupSubjectsModel?.length || 0}</Badge>
        </Tab>
        <Tab>
          멤버 <Badge size="small">{memberSubjectsModel?.length || 0}</Badge>
        </Tab>
      </TabList>
      <TabPanelList>
        <TabPanel>
          {isGroupQueryError && (
            <Flex alignItems="center" height="350px" justifyContent="center">
              검색 결과를 불러오지 못했습니다.
              <TextLink onClick={() => groupRefetch()}>다시 불러오기</TextLink>
            </Flex>
          )}
          {groupSubjectsModel && !groupSubjectsModel.length && (
            <Flex alignItems="center" height="350px" justifyContent="center">
              <Text color="textNeutralBase" fontWeight="medium">{`'${keyword}'`}</Text> 검색 결과가
              없습니다.
            </Flex>
          )}
          {groupSubjectsModel && groupSubjectsModel.length > 0 && (
            <SearchedGroupList>
              {groupSubjectsModel?.map((group) => (
                <ListItem
                  disabled={group.disabled}
                  isSelected={group.isSelected}
                  key={group.role.subjectId}
                  role={group.role}
                  tooltipContent={group.tooltipContent}
                  onSelect={() => onToggle(group.role)}
                />
              ))}
            </SearchedGroupList>
          )}
        </TabPanel>
        <TabPanel>
          {isMemberQueryError && (
            <Flex alignItems="center" height="350px" justifyContent="center">
              검색 결과를 불러오지 못했습니다.
              <TextLink onClick={() => memberRefetch()}>다시 불러오기</TextLink>
            </Flex>
          )}
          {memberSubjectsModel && !memberSubjectsModel.length && (
            <Flex alignItems="center" height="350px" justifyContent="center">
              <Text color="textNeutralBase" fontWeight="medium">{`'${keyword}'`}</Text> 검색 결과가
              없습니다.
            </Flex>
          )}
          {memberSubjectsModel && memberSubjectsModel.length > 0 && (
            <SearchedGroupList>
              {memberSubjectsModel?.map((member) => (
                <ListItem
                  disabled={member.disabled}
                  isSelected={member.isSelected}
                  key={member.role.subjectId}
                  role={member.role}
                  tooltipContent={member.tooltipContent}
                  onSelect={() => onToggle(member.role)}
                />
              ))}
            </SearchedGroupList>
          )}
        </TabPanel>
      </TabPanelList>
    </UnderlineTabs>
  );
}

function SearchedGroupList({ children }: { children: ReactNode }) {
  return (
    <Flex
      as="ul"
      borderBottom="1px solid"
      borderBottomColor="tableBorderNeutralBase"
      flexDirection="column"
      height="350px"
      overflowY="auto"
    >
      {children}
    </Flex>
  );
}

interface ListItemProps {
  disabled?: boolean;
  role: ResourceRoleSubject;
  isSelected: boolean;
  tooltipContent?: ReactNode;
  onSelect: (checked: boolean) => void;
}

function ListItem({ disabled, isSelected, role, tooltipContent, onSelect }: ListItemProps) {
  return (
    <Flex
      as="li"
      borderBottom="1px solid"
      borderBottomColor="tableBorderNeutralLight"
      sx={{
        '&:hover': {
          bg: 'tableBackgroundNeutralSlightlyDark',
        },
      }}
      width="100%"
    >
      <Flex
        alignItems="center"
        as="label"
        sx={{ gap: 4, p: 4, width: '100%', '&:hover': { cursor: 'pointer' } }}
      >
        <Tooltip content={tooltipContent} disabled={!disabled} placement="top-start" scale="sm">
          <Checkbox
            checked={isSelected}
            disabled={disabled}
            width="14px"
            onChange={(event) => onSelect(event.target.checked)}
          />
        </Tooltip>
        <ResourceRoleSubjectLabel role={role} />
      </Flex>
    </Flex>
  );
}
